import { Button, Menu, Badge, EmptyState } from '@cinesend/ui-kit'
import DataTable from '../../components/data_table'
import { Plus } from '@phosphor-icons/react'
import RowMenu from '../../components/row_menu'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import useStore from '../../hooks/use_store'
import useUtilities from '../../hooks/use_utilities'
import useTableFilters from '../../hooks/use_table_filters'

// Maybe pull from backend, or not since it's a very limited list?
const FIELD_TYPES = ['String', 'Boolean', 'Datetime', 'Dropdown', 'Image', 'Video', 'Audio', 'Number']

const Fields = () => {
  const navigate = useNavigate()
  const schemaID = useParams().schemaID
  const queryClient = useQueryClient()
  const { assetTypes } = useUtilities()

  const filteringOptions = [
    {
      label: 'Field Type',
      key: 'field_type',
      type: 'checkboxes',
      options: FIELD_TYPES.map((type) => ({
        label: type,
        key: type.toLowerCase(),
        value: type.toLowerCase(),
      })),
    },
    {
      label: 'Asset Types',
      key: 'asset_types',
      type: 'checkboxes',
      options:
        assetTypes?.map((type) => ({
          label: type.plural_label,
          key: type.value,
          value: type.value,
        })) || [],
    },
    {
      label: 'Localizable',
      key: 'is_localizable',
      type: 'checkboxes',
      options: [
        { label: 'Yes', key: 'true', value: 'true' },
        { label: 'No', key: 'false', value: 'false' },
      ],
    },
    {
      label: 'External API Source',
      key: 'is_external_api_value_source',
      type: 'checkboxes',
      options: [
        { label: 'Yes', key: 'true', value: 'true' },
        { label: 'No', key: 'false', value: 'false' },
      ],
    },
    {
      label: 'Size Field',
      key: 'is_size_field',
      type: 'checkboxes',
      options: [
        { label: 'Yes', key: 'true', value: 'true' },
        { label: 'No', key: 'false', value: 'false' },
      ],
    },
  ]
  const { queryString, searching, filtering } = useTableFilters({
    filteringOptions,
    enableSearch: true,
    queryExists: true,
  })

  const { data: list, isLoading, isError, error, refetch } = useQuery([`fields?schemaID=${schemaID}`, queryString])
  const { createModel, deleteModel } = useStore()
  const createField = (type) => {
    // Default to all available asset types since root schema is used by many airlines
    const defaultAssetTypes = assetTypes?.map((assetType) => assetType.value) || []

    createModel.mutate(
      {
        endpoint: 'fields',
        data: {
          schema_id: schemaID,
          name: 'New Field',
          field_type: type.toLowerCase(),
          asset_types: defaultAssetTypes,
        },
      },
      {
        onSuccess: () => queryClient.invalidateQueries([`fields?schemaID=${schemaID}`]),
      }
    )
  }
  const deleteField = (field) => {
    deleteModel.mutate(
      {
        endpoint: 'fields',
        id: field.id,
        deleteNoun: 'field',
        deleteName: field.name,
      },
      {
        onSuccess: () => queryClient.invalidateQueries([`fields?schemaID=${schemaID}`]),
      }
    )
  }

  const prepareValidators = (validators) => {
    return (
      <div className='flex flex-wrap gap-1'>
        {validators.map((validator) => {
          const label =
            validator.parameters !== null ? `${validator.name}: ${validator.parameters.value}` : validator.name
          return <Badge key={validator.id}>{label}</Badge>
        })}
      </div>
    )
  }

  const columns = [
    { key: 'name', header: 'Field identifier', render: (data) => data.name },
    {
      key: 'field_type',
      header: 'Field type',
      render: (data) => <Badge>{data.field_type.toUpperCase()}</Badge>,
    },
    {
      key: 'asset_types',
      header: 'Asset types',
      render: (data) => (
        <Badge tone='warning'>{getAssetTypes(assetTypes || [], data.asset_types || []).toUpperCase()}</Badge>
      ),
    },
    {
      key: 'flags',
      header: 'Field Flags',
      render: (data) => (
        <div className='flex flex-wrap gap-1 p-1'>
          {data.is_size_field == true && <Badge tone='info'>Size field</Badge>}
          {data.is_localizable == true && <Badge tone='info'>Localizable</Badge>}
          {data.is_external_api_value_source == true && <Badge tone='info'>External Source</Badge>}
        </div>
      ),
    },
    { key: 'validators', header: 'Validators', render: (data) => prepareValidators(data.enabled_validators) },
    {
      key: 'actions',
      header: '',
      width: '65px',
      render: (data) => (
        <RowMenu
          label={`Actions for ${data.name}`}
          items={[
            {
              id: 'delete',
              label: 'Delete',
              tone: 'danger',
              onSelect: () => deleteField(data),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <div className='border bg-info-100 p-4 text-xs rounded my-4'>
        This is the list of fields that are attached to this schema. A schema can be added to any cycle, but once any
        metadata is populated, that schema is locked and fields cannot be modified.
      </div>
      <DataTable
        caption='Fields'
        columns={columns}
        rows={list?.data ?? []}
        onRowClick={(data) => navigate(`${data.id}`)}
        isLoading={isLoading}
        error={isError ? error : null}
        onRetry={refetch}
        empty={<EmptyState title='No cycles found!' detail='Create a new cycle to begin.' />}
        filtering={filtering}
        searching={searching}
        actions={
          <Menu
            trigger={<Button leadingIcon={<Plus size={20} />}>Add Field</Button>}
            items={FIELD_TYPES.map((type) => ({ id: type, label: type, onSelect: () => createField(type) }))}
          />
        }
      />
    </>
  )
}

const getAssetTypes = (allAssetTypes, specifiedAssetTypes) => {
  if (specifiedAssetTypes.length === 0) {
    return 'All Asset Types'
  }
  return allAssetTypes
    .filter((assetType) => specifiedAssetTypes.includes(assetType.value))
    .map((assetType) => assetType.plural_label)
    .join(', ')
}

export default Fields
